/**
 * Appearance management utilities for workspace serialization/deserialization
 */

import {
  EXTENDED_EDGE_DEFAULTS,
  DEFAULT_APPEARANCE_COLORS,
} from "../../chartLib/lib/interactive/commonDefaults.js";

/**
 * Serialize appearance configuration for storage
 * @param {Object} appearance - Appearance configuration
 * @returns {Object} - Serialized appearance
 */
export const serializeAppearance = (appearance) => {
  if (!appearance) return {};

  const serialized = { ...appearance };

  // For indicators, preserve stroke as object since LineSeries and other components expect it
  // For interactive objects, we may need different handling based on component type
  // For now, preserve the original structure and let components handle conversion
  if (appearance.stroke && typeof appearance.stroke === "object") {
    serialized.stroke = { ...appearance.stroke };
  } else if (typeof appearance.stroke === "string") {
    // If it's already a string, keep it as is
    serialized.stroke = appearance.stroke;
  }

  // Handle fill similarly
  if (appearance.fill && typeof appearance.fill === "object") {
    serialized.fill = { ...appearance.fill };
  } else if (typeof appearance.fill === "string") {
    serialized.fill = appearance.fill;
  }

  return serialized;
};

/**
 * Ensure appearance configuration has valid properties for deserialization
 * @param {Object} appearance - Appearance configuration
 * @returns {Object} - Valid appearance with defaults
 */
export const ensureValidAppearance = (appearance) => {
  // If appearance is completely missing, create a default one with all required properties
  if (!appearance) {
    return {
      stroke: {
        color: "#9C27B0",
        dash: DEFAULT_APPEARANCE_COLORS.defaultStrokeDash,
        width: DEFAULT_APPEARANCE_COLORS.defaultStrokeWidth,
        opacity: DEFAULT_APPEARANCE_COLORS.defaultStrokeOpacity,
      },
      fill: {
        color: "#9C27B0",
        opacity: DEFAULT_APPEARANCE_COLORS.defaultFillOpacity,
      },
      fillOpacity: DEFAULT_APPEARANCE_COLORS.defaultFillOpacity,
      ...EXTENDED_EDGE_DEFAULTS,
    };
  }

  const validAppearance = { ...appearance };

  // For stroke: preserve object structure for indicators, ensure valid string for interactive objects
  if (validAppearance.stroke) {
    if (typeof validAppearance.stroke === "object") {
      // Ensure object has required properties
      validAppearance.stroke = {
        color:
          validAppearance.stroke.color ||
          DEFAULT_APPEARANCE_COLORS.defaultStrokeColor,
        width:
          validAppearance.stroke.width ||
          DEFAULT_APPEARANCE_COLORS.defaultStrokeWidth,
        dash:
          validAppearance.stroke.dash ||
          DEFAULT_APPEARANCE_COLORS.defaultStrokeDash,
        opacity:
          validAppearance.stroke.opacity !== undefined
            ? validAppearance.stroke.opacity
            : DEFAULT_APPEARANCE_COLORS.defaultStrokeOpacity,
      };
    } else if (typeof validAppearance.stroke === "string") {
      // Keep string as is - no change needed
    } else {
      // Default to object structure for indicators
      validAppearance.stroke = {
        color: DEFAULT_APPEARANCE_COLORS.defaultStrokeColor,
        width: DEFAULT_APPEARANCE_COLORS.defaultStrokeWidth,
        dash: DEFAULT_APPEARANCE_COLORS.defaultStrokeDash,
        opacity: DEFAULT_APPEARANCE_COLORS.defaultStrokeOpacity,
      };
    }
  } else {
    // Default stroke object
    validAppearance.stroke = {
      color: DEFAULT_APPEARANCE_COLORS.defaultStrokeColor,
      width: DEFAULT_APPEARANCE_COLORS.defaultStrokeWidth,
      dash: DEFAULT_APPEARANCE_COLORS.defaultStrokeDash,
      opacity: DEFAULT_APPEARANCE_COLORS.defaultStrokeOpacity,
    };
  }

  // For fill: preserve object structure for interactive objects, ensure valid string for simple cases
  if (validAppearance.fill) {
    if (typeof validAppearance.fill === "object") {
      // Ensure object has required properties
      validAppearance.fill = {
        color:
          validAppearance.fill.color ||
          DEFAULT_APPEARANCE_COLORS.defaultFillColor,
        opacity:
          validAppearance.fill.opacity !== undefined
            ? validAppearance.fill.opacity
            : DEFAULT_APPEARANCE_COLORS.defaultFillOpacity,
      };
    } else if (typeof validAppearance.fill === "string") {
      // Convert string to object structure for consistency
      validAppearance.fill = {
        color: validAppearance.fill,
        opacity: DEFAULT_APPEARANCE_COLORS.defaultFillOpacity,
      };
    } else {
      // Default fill object
      validAppearance.fill = {
        color: DEFAULT_APPEARANCE_COLORS.defaultFillColor,
        opacity: DEFAULT_APPEARANCE_COLORS.defaultFillOpacity,
      };
    }
  } else {
    // Default fill object
    validAppearance.fill = {
      color: DEFAULT_APPEARANCE_COLORS.defaultFillColor,
      opacity: DEFAULT_APPEARANCE_COLORS.defaultFillOpacity,
    };
  }

  // Ensure other common properties have defaults from EXTENDED_EDGE_DEFAULTS
  if (validAppearance.fillOpacity === undefined) {
    validAppearance.fillOpacity = DEFAULT_APPEARANCE_COLORS.defaultFillOpacity;
  }
  if (validAppearance.edgeFill === undefined) {
    validAppearance.edgeFill = EXTENDED_EDGE_DEFAULTS.edgeFill;
  }
  if (validAppearance.nsEdgeFill === undefined) {
    validAppearance.nsEdgeFill = EXTENDED_EDGE_DEFAULTS.nsEdgeFill;
  }
  if (validAppearance.edgeFill2 === undefined) {
    validAppearance.edgeFill2 = EXTENDED_EDGE_DEFAULTS.edgeFill2;
  }
  if (
    !validAppearance.edgeStroke ||
    typeof validAppearance.edgeStroke !== "object"
  ) {
    validAppearance.edgeStroke = EXTENDED_EDGE_DEFAULTS.edgeStroke;
  }
  if (validAppearance.r === undefined) {
    validAppearance.r = EXTENDED_EDGE_DEFAULTS.r;
  }
  if (validAppearance.width === undefined) {
    validAppearance.width = DEFAULT_APPEARANCE_COLORS.defaultStrokeWidth;
  }
  if (validAppearance.opacity === undefined) {
    validAppearance.opacity = DEFAULT_APPEARANCE_COLORS.defaultStrokeOpacity;
  }

  return validAppearance;
};
